<template>
  <div
    id="liveContainer"
    ref="liveContainer"
    :class="containerClassName"
    :data-test="`producer-output-${output}`"
  >
    <div class="header panel-drag-handle">
      <div class="header-section">
        <div :class="[
          'output-indicator',
          `output-indicator--${output}`
        ]">
          <span>
            {{ output === 'live' ? 'L' : 'P' }}
          </span>
        </div>

        <template v-if="showLiveVideoTimer">
          <div class="countdown margin-left-md" :data-test="`producer-output-${output}-videoTimer`">
            {{ videoTimestamp | toMinutesAndSeconds }} / {{ videoDuration | toMinutesAndSeconds }}
          </div>
        </template>

        <template v-if="showPreviewVideoTimer">
          <div class="countdown margin-left-md" :data-test="`producer-output-${output}-videoTimer`">
            {{ videoTimestamp | toMinutesAndSeconds }} / {{ videoDuration | toMinutesAndSeconds }}
          </div>
        </template>

        <div
          v-if="output === 'live' && isOutputActive"
          class="flex-grow text-right"
        >
          <button
            title="Export Output"
            class="tb-btn-text margin-left-xxs export-output-button"
            data-entity="producer-exportLive"
            data-test="producer-output-export"
            :disabled="isExporting"
            @click.stop="exportLive"
          >
            <span v-if="!isExporting" class="fak fa-regular-image-circle-arrow-down" />
            <span v-else class="fas fa-spinner-third margin-right-sm" />
            {{ isExporting ? 'Capturing snapshot...' : 'Take snapshot' }}
          </button>
        </div>
      </div>
    </div>

    <div
      ref="iframeContainer"
      :class="iframeContainerClasses"
    >
      <iframe
        :key="production.id"
        ref="liveIframe"
        :class="iframeClasses"
        allow="autoplay"
        loading="lazy"
        :src="output === 'live' ? liveUrl : previewUrl"
        :title="output === 'live' ? 'Live Output' : 'Preview Output'"
        :style="{
          height: `${iframeHeight}px`,
          width: `${iframeWidth}px`,
        }"
        :data-test="output === 'live' ? 'producer-live-iframe' : 'producer-preview-iframe'"
        :data-is-active="`${isOutputActive}`"
        :data-active-item="`${production.index}`"
      />
    </div>
  </div>
</template>

<script lang="js">
import dayjs from 'dayjs';
import { mapState, mapGetters } from 'vuex';

import Timer from '../utils/timer';
import TimelineMixins from '../mixins/timeline-mixins';

export default {
  props: {
    output: {
      type: String,
      default: 'live',
    },
    iframeWidth: {
      type: Number,
      default: 1920,
    },
    iframeHeight: {
      type: Number,
      default: 1080,
    },
  },

  mixins: [TimelineMixins],

  data() {
    return {
      videoTimestamp: 0,
      resizeObserver: undefined,
      savedWrapperSize: {},
      isExporting: false,
    };
  },

  mounted() {
    this.handleOnResizeWindow();
    this.initializeResizeObserver();

    this.$store.dispatch('setupLive');
    this.$store.dispatch('setupPreview');

    this.startTimer();
  },

  beforeDestroy() {
    this.unobserve();
    this.resetTimer();
  },

  computed: {
    ...mapGetters(['liveUrl', 'previewUrl']),

    ...mapState({
      videoDurationLive: (state) => state.production.videoDuration,
      videoDurationPreview: (state) => state.preview.videoDuration,
      videoPlayingLive: (state) => state.production.production.videoPlaying,
      videoPlayingPreview: (state) => state.preview.production.videoPlaying,
    }),

    videoDuration() {
      return this.output === 'live'
        ? this.videoDurationLive
        : this.videoDurationPreview;
    },

    videoPlaying() {
      return this.output === 'live'
        ? this.videoPlayingLive
        : this.videoPlayingPreview;
    },

    production() {
      return this.output === 'live'
        ? this.$store.state.production.production
        : this.$store.state.preview.production;
    },

    isOutputActive() {
      return this.output === 'live'
        ? this.liveDisplayActive
        : this.previewDisplayActive;
    },

    containerClassName() {
      return {
        [`output-window`]: true,
        collapsed: this.collapsed,
      };
    },

    iframeContainerClasses() {
      return {
        'output-window__iframe-container': true,
      };
    },

    iframeClasses() {
      return {
        'output-window__iframe': true,
      };
    },

    showLiveVideoTimer() {
      // Only show countdown if we have a video duration
      // Might need to add some further checks since producer doesn't know what the post is right now.
      return (
        this.videoDuration
        && this.output === 'live'
      );
    },

    showPreviewVideoTimer() {
      // Only show countdown if we have a video duration
      // Might need to add some further checks since producer doesn't know what the post is right now.
      return (
        this.videoDuration
        && this.output === 'preview'
      );
    },

    /**
    * Unique string to represent live element + live post
    */
    liveKey() {
      const { index, sub_index: subIndex } = this.production;
      return `${index}-${subIndex}`;
    },
  },

  watch: {
    // If the live element changes, then reset everything
    liveKey() {
      this.resetTimer();
    },

    videoPlaying(videoPlaying) {
      if (!this.timer) {
        // Start a new timer
        this.startTimer();
        return;
      }

      // Start/Stop the existing timer
      if (videoPlaying) {
        this.timer.start();
      } else {
        this.timer.pause();
      }
    },

    videoDuration() {
      // If video duration changes, then we need a new timer entirely.
      // Because videos don't magically change their own duration.
      this.startTimer();
    },

    iframeHeight() {
      this.handleOnResizeWindow();
    },

    iframeWidth() {
      this.handleOnResizeWindow();
    },
  },

  methods: {
    exportLive() {
      this.isExporting = true;

      // We could actually just download directly from the export URL,
      // but this feels simpler for error handling (for now; subject to change).
      fetch(`${process.env.MONO_API}/productions/${this.production.id}/export?timezone=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`, {
        credentials: 'include',
        mode: 'cors',
      })
      .then(async (resp) => {
        if (!resp.ok) {
          const body = await resp.text();
          console.error('Failed to export live output:', body);
          this.errorToast('There was an error exporting your live output.');
          return;
        }

        // Create a new Object URL out of the raw image.
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);

        const filename = `${this.production.title.replace(/\s+/g, '-')}_${dayjs().format('YYYY-MM-DD_HH[h]mm[m]ss[s]')}.png`;

        // Then download the thing.
        const el = document.createElement('a');
        el.setAttribute('href', url);
        el.setAttribute('download', filename);
        el.click();

        this.successToast('Your Live Output has successfully been exported as a PNG! Check your browser or downloads folder to grab the file.');
      })
      .catch((err) => {
        console.error('Failed to export live output:', err);
        this.errorToast('Uh-oh, we couldn’t export your Live Output as a PNG. Please try again.');
      })
      .finally(() => {
        this.isExporting = false;
      });
    },

    /**
    * If there's a video playing and we have a video duration, then start timer.
    * Every tick of the timer will update `videoTimestamp` with the current time.
    * This is just a rough estimate of where the video is at in the output(s)
    */
    startTimer() {
      // Stop any existing timer so we stop the tick events
      this.resetTimer();

      if (!this.videoPlaying || !this.videoDuration) {
        return;
      }

      this.timer = new Timer({
        duration: this.videoDuration,

        onTick: (ts) => {
          this.videoTimestamp = ts;
        },

        onDone: () => {
          this.videoTimestamp = this.videoDuration;
          this.resetTimer();
        },
      });
    },

    resetTimer() {
      this.timer?.pause();
      this.timer = null;
      this.videoTimestamp = 0;
    },

    initializeResizeObserver() {
      // resize observer only runs once per animation frame, so there
      // is little need to debounce this observer.
      this.resizeObserver = new ResizeObserver(this.handleOnResizeWindow);
      this.resizeObserver.observe(this.$refs.liveContainer);
    },

    handleOnResizeWindow() {
      const container = this.$refs.iframeContainer;
      const iframe = this.$refs.liveIframe;

      if (!iframe) { return; }

      const height = container.clientHeight;
      const width = container.clientWidth;

      const containerAspectRatio = width / height;
      const iframeAspectRatio = this.iframeWidth / this.iframeHeight;

      let scale = 1;

      if (containerAspectRatio > iframeAspectRatio) {
        scale = height / this.iframeHeight;
      } else {
        scale = width / this.iframeWidth;
      }

      iframe.style.transform = `scale(${scale}) translate(-50%, -50%)`;
    },

    unobserve() {
      if (this.resizeObserver) {
        this.resizeObserver.unobserve(this.$refs.liveContainer);
      }
    },
  },
};
</script>
